"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { Button, ScrollArea } from "@ui/shad"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { useViewportFill } from "@/hooks/use-viewport-fill"

import { cn } from "../utils"

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { ref: fillRef, style } = useViewportFill()

  const handleRefs = (element: HTMLDivElement | null) => {
    fillRef.current = element
    if (typeof ref === "function") {
      ref(element)
    } else if (ref) {
      ref.current = element
    }
  }

  return <TabsPrimitive.Root ref={handleRefs} className={className} style={style} {...props} />
})
Tabs.displayName = TabsPrimitive.Root.displayName

function useScrollNavigation(scrollRef: React.RefObject<HTMLDivElement | null>) {
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }, [scrollRef])

  React.useEffect(() => {
    updateScrollState()
    const el = scrollRef.current
    if (!el) return

    el.addEventListener("scroll", updateScrollState)
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)

    return () => {
      el.removeEventListener("scroll", updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState, scrollRef])

  const scroll = React.useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current
      if (!el) return

      const scrollAmount = el.clientWidth * 0.8
      el.scrollTo({
        left: el.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      })
    },
    [scrollRef]
  )

  return { canScrollLeft, canScrollRight, scroll }
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    noBorder?: boolean
    actions?: React.ReactNode
  }
>(({ className, children, noBorder, actions, ...props }, ref) => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, scroll } = useScrollNavigation(scrollRef)

  return (
    <div className={cn(!noBorder && "border-b")}>
      <div className="relative flex w-full items-center">
        {canScrollLeft && (
          <Button
            onClick={() => scroll("left")}
            variant="ghost"
            size="iconXs"
            className="bg-background absolute top-2 left-1 z-10"
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}

        <div ref={scrollRef} className="flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsPrimitive.List
            ref={ref}
            className={cn(
              "text-muted-foreground inline-flex w-max items-center justify-start space-x-2 p-1 pb-0",
              className
            )}
            {...props}
          >
            {children}
          </TabsPrimitive.List>
        </div>

        {canScrollRight && (
          <Button
            onClick={() => scroll("right")}
            variant="ghost"
            size="iconXs"
            className="bg-background absolute top-2 right-1 z-10"
          >
            <ChevronRight className="size-4" />
          </Button>
        )}

        {actions && <div className="hidden shrink-0 items-center gap-2 pr-1 pb-1 min-[900px]:flex">{actions}</div>}
      </div>
      {actions && <div className="flex items-center justify-end gap-2 pt-2 pb-1 min-[900px]:hidden">{actions}</div>}
    </div>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    icon?: React.ReactNode
  }
>(({ className, icon, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "hover:text-foreground data-[state=active]:text-foreground data-[state=active]:after:bg-foreground relative inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-all focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 data-[state=active]:after:absolute data-[state=active]:after:-bottom-px data-[state=active]:after:left-0 data-[state=active]:after:h-0.5 data-[state=active]:after:w-full data-[state=active]:after:rounded-full [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className
    )}
    {...props}
  >
    {icon}
    {props.children}
  </TabsPrimitive.Trigger>
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
    disableMaxHeight?: boolean
    disableScrollArea?: boolean
  }
>(({ className, disableMaxHeight = false, disableScrollArea = false, ...props }, ref) => {
  const combinedClassName = disableMaxHeight ? className : cn("h-[calc(100dvh-var(--topOffset))]", className)

  if (disableScrollArea) {
    return (
      <TabsPrimitive.Content
        ref={ref}
        className={cn(
          "ring-offset-background focus-visible:ring-ring relative focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden data-[state=inactive]:hidden",
          combinedClassName
        )}
        {...props}
      />
    )
  }

  return (
    <TabsPrimitive.Content ref={ref} asChild value={props.value}>
      <ScrollArea
        className={cn(
          "ring-offset-background focus-visible:ring-ring relative focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
          combinedClassName
        )}
      >
        <div {...props} />
      </ScrollArea>
    </TabsPrimitive.Content>
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
